"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const idm_dataset_service_client_js_1 = require("@synisys/idm-dataset-service-client-js");
const idm_common_util_frontend_1 = require("@synisys/idm-common-util-frontend");
const common_helper_functions_1 = require("../views/shared/common-helper-functions");
const idm_ng_controls_1 = require("@synisys/idm-ng-controls");
const path_color_service_1 = require("../views/chart/shared/service/path-color.service");
/**
 * Verify whether the DTO is applicable for this component based on some predefined criteria (e.g. paths not shown
 * "everywhere" are excluded).
 * @param {PathDto} path - The object to be inspected.
 * @param {boolean} isAllMeasures
 * @returns {boolean} True if the path is applicable, false otherwise.
 * @see AppearanceType
 * @see CategoryType *
 */
function isAvailablePath(path, isAllMeasures) {
    if (path.appearanceType !== idm_dataset_service_client_js_1.AppearanceType.EVERYWHERE && path.appearanceType !== idm_dataset_service_client_js_1.AppearanceType.DATA) {
        return false;
    }
    else if (path instanceof idm_dataset_service_client_js_1.CategoryPathDto &&
        path.categoryType !== idm_dataset_service_client_js_1.CategoryType.STANDARD &&
        path.categoryType !== idm_dataset_service_client_js_1.CategoryType.DATE &&
        path.categoryType !== idm_dataset_service_client_js_1.CategoryType.INDICATOR &&
        path.categoryType !== idm_dataset_service_client_js_1.CategoryType.CURRENCY &&
        path.categoryType !== idm_dataset_service_client_js_1.CategoryType.DOCUMENT &&
        path.categoryType !== idm_dataset_service_client_js_1.CategoryType.FRAMEWORK &&
        path.categoryType !== idm_dataset_service_client_js_1.CategoryType.DISAGGREGATION &&
        path.categoryType !== idm_dataset_service_client_js_1.CategoryType.ME_ASSETS) {
        return false;
    }
    else if (!isAllMeasures && path instanceof idm_dataset_service_client_js_1.MeasurePathDto && path.measurePathType !== idm_dataset_service_client_js_1.MeasurePathType.NUMBER && path.measurePathType !== idm_dataset_service_client_js_1.MeasurePathType.MONEY) {
        return false;
    }
    return true;
}
exports.isAvailablePath = isAvailablePath;
function isCurrencyMeasurePath(path) {
    return path instanceof idm_dataset_service_client_js_1.MeasurePathDto && path.measurePathType === idm_dataset_service_client_js_1.MeasurePathType.MONEY;
}
exports.isCurrencyMeasurePath = isCurrencyMeasurePath;
/**
 * Copy over paths from one map to another, filtering those not applicable to this component (e.g. paths not shown
 * "everywhere" are excluded).
 * @param {PathsMap} source - The source map to copy from.
 * @param {PathsMap} target - The destination map to copy to.
 * @param {boolean} isAllMeasures
 */
function extractAvailablePaths(source, target, isAllMeasures = false) {
    source.forEach((path) => {
        if (isAvailablePath(path, isAllMeasures)) {
            target.set(path.id, path);
        }
    });
}
exports.extractAvailablePaths = extractAvailablePaths;
/**
 *
 * @param {PathsMap} paths
 * @param {Array<PathGroupDto>} groups
 * @param {Language} currentLanguage
 * @param {string} defaultGroupMessage
 * @param {any[]} colorPalette
 * @param {number} defaultCurrencyId
 * @returns {ComboboxOptions<ComboboxGroup<ComboboxOptions<ComboboxOptionWithColor<any, any>>, any>>}
 */
function initGroupedComboOptions(paths, groups, currentLanguage, defaultGroupMessage, colorPalette, defaultCurrencyId = null) {
    idm_common_util_frontend_1.PreconditionCheck.notNullOrUndefined(paths);
    idm_common_util_frontend_1.PreconditionCheck.notNullOrUndefined(groups);
    idm_common_util_frontend_1.PreconditionCheck.notNullOrUndefined(currentLanguage);
    idm_common_util_frontend_1.PreconditionCheck.notNullOrUndefined(defaultGroupMessage);
    let options = [];
    let groupedPaths = new Map();
    let grouping = [];
    let counter = 0;
    groups.forEach((group) => {
        let groupName = group.name.get(currentLanguage.getId());
        grouping = [];
        group.paths.forEach((orderedPath) => {
            let pathId = orderedPath.pathId;
            if (paths.has(pathId)) {
                let path = paths.get(pathId);
                let pathName = path.name.get(currentLanguage.getId());
                let pathValue;
                if (isCurrencyMeasurePath(path)) {
                    pathValue = Object.assign({}, path, { currencyId: defaultCurrencyId });
                }
                else {
                    pathValue = Object.assign({}, path);
                }
                colorPalette ? grouping.push(new idm_ng_controls_1.ComboboxOptionWithColor(pathValue, pathName || '', colorPalette[counter++ % colorPalette.length])) : grouping.push(new idm_ng_controls_1.ComboboxOption(pathValue, pathName));
                colorPalette && path_color_service_1.PathColorService.setMeasurePathColorMap(path.id, colorPalette[(counter - 1) % colorPalette.length]);
                groupedPaths.set(pathId, group.id);
            }
        });
        grouping.length !== 0 && options.push(new idm_ng_controls_1.ComboboxGroup(grouping, groupName || ''));
    });
    grouping = [];
    paths.forEach((path) => {
        if (!groupedPaths.has(path.id)) {
            let pathValue;
            if (isCurrencyMeasurePath(path)) {
                pathValue = Object.assign({}, path, { currencyId: defaultCurrencyId });
            }
            else {
                pathValue = Object.assign({}, path);
            }
            colorPalette ? grouping.push(new idm_ng_controls_1.ComboboxOptionWithColor(pathValue, path.name.get(currentLanguage.getId()) || '', colorPalette[counter++ % colorPalette.length])) : grouping.push(new idm_ng_controls_1.ComboboxOption(pathValue, path.name.get(currentLanguage.getId()) || ''));
            colorPalette && path_color_service_1.PathColorService.setMeasurePathColorMap(path.id, colorPalette[(counter - 1) % colorPalette.length]);
        }
    });
    options.push(new idm_ng_controls_1.ComboboxGroup(grouping, defaultGroupMessage));
    return options;
}
exports.initGroupedComboOptions = initGroupedComboOptions;
function initComboBoxOptions(items) {
    idm_common_util_frontend_1.PreconditionCheck.notNullOrUndefined(items);
    const options = [];
    items.forEach((item) => {
        options.push(new idm_ng_controls_1.ComboboxOption(item['id'], item['name']));
    });
    return options;
}
exports.initComboBoxOptions = initComboBoxOptions;
function setCurrenciesOfMeasures(groupedData, selectedItems) {
    let data = [];
    groupedData.forEach((group) => {
        data.push(group.items);
    });
    return selectedItems.map((item) => {
        let foundItem = data.find(dataItem => dataItem.id === item.id && dataItem.currencyId === item.currencyId);
        return Object.assign({}, item, { currencyId: item.currencyId ? item.currencyId : foundItem ? foundItem.currencyId : null });
    });
}
exports.setCurrenciesOfMeasures = setCurrenciesOfMeasures;
function filterGroupedDataBySelectedItemId(groupedData, selectedItemId) {
    return groupedData.map((group) => {
        return {
            name: group.name,
            items: group.items.filter((item) => {
                return item.value.id !== selectedItemId;
            })
        };
    }).filter((group) => {
        return group.items.length > 0;
    });
}
exports.filterGroupedDataBySelectedItemId = filterGroupedDataBySelectedItemId;
/**
 * Load paths' groups through the service. Relies on the current sate of the component to have data-set identifier
 * and appropriate service. Sorts the paths within groups in accordance to the specific property.
 * @returns {Promise<Array<PathGroupDto>>} The array of groups with already sorted paths.
 * @see PathService
 */
function loadPathGroups(dataset, pathService) {
    idm_common_util_frontend_1.PreconditionCheck.notNullOrUndefined(dataset);
    idm_common_util_frontend_1.PreconditionCheck.notNullOrUndefined(pathService);
    return pathService.getPathGroups(dataset)
        .then((groups) => {
        groups.forEach((group) => {
            group.paths.sort((path1, path2) => {
                return path1.sortId - path2.sortId;
            });
        });
        return groups;
    })
        .catch((err) => {
        return common_helper_functions_1.defaultPromiseErrorHandler(err);
    });
}
exports.loadPathGroups = loadPathGroups;
/**
 * Loads dataSet name by dataSet id
 * @returns {Promise<Map<number, string>>} The promise to fetch the dataSet's name.
 * @see DatasetService
 */
function loadDatasetById(dataset, datasetService) {
    idm_common_util_frontend_1.PreconditionCheck.notNullOrUndefined(dataset);
    idm_common_util_frontend_1.PreconditionCheck.notNullOrUndefined(datasetService);
    return datasetService.getDataset(dataset)
        .then((datasetDto) => {
        return datasetDto.name;
    })
        .catch((err) => {
        return common_helper_functions_1.defaultPromiseErrorHandler(err);
    });
}
exports.loadDatasetById = loadDatasetById;
/**
 * Load all the paths through the service in a transfer container. Relies on the current sate of the component to
 * have data-set identifier and appropriate service.
 * @returns {Promise<PathsDto>} The promise to fetch the paths's container.
 * @see PathService
 */
function loadPaths(dataset, pathService) {
    idm_common_util_frontend_1.PreconditionCheck.notNullOrUndefined(dataset);
    idm_common_util_frontend_1.PreconditionCheck.notNullOrUndefined(pathService);
    return pathService.getPaths(dataset)
        .catch((err) => {
        return common_helper_functions_1.defaultPromiseErrorHandler(err);
    });
}
exports.loadPaths = loadPaths;
/**
 * Converts and filters page items
 * @param pageId - current page id
 * @param pages - page items
 * @param currentLanguageId
 * @returns {Array<ComboItem>} converted and filtered combo items
 */
function processPageItems(pageId, pages, currentLanguageId) {
    /*
        flatten pages and page children
     */
    const flattenedPages = pages.reduce((acc, current) => {
        return acc.concat(current, current.children);
    }, []);
    /*
        filter pages by current pageId and convert pages to comboItem
     */
    return flattenedPages.reduce((acc, current) => {
        let isFiltered;
        if (pageId instanceof Array) {
            isFiltered = pageId.includes(Number.parseInt(current.routLink));
        }
        else {
            isFiltered = current.routLink !== pageId;
        }
        return isFiltered ?
            acc.concat({
                name: current.title.getValue(currentLanguageId),
                value: Number.parseInt(current.routLink)
            }) : acc;
    }, []);
}
exports.processPageItems = processPageItems;
function createMeasureListItem(measure) {
    return {
        id: measure.value.id,
        name: measure.name,
        order: measure.value.sortId,
        color: measure.color,
        currencyId: measure.value.currencyId,
        value: measure.value
    };
}
exports.createMeasureListItem = createMeasureListItem;
//# sourceMappingURL=path-processing-helper.js.map